/**
 * FIND-02 — Cmd+K switcher index & ranking (spec §4.2 / §5.1). Pure and framework-free: builds the
 * flat row set (titles ∪ aliases ∪ tags ∪ entity names) from the local meta/entity data, scores rows
 * against the typed query with a VS Code-style fuzzy matcher, and applies the ranking tiers
 * (adaptive-history → fuzzy score → frecency tiebreak). The React/cmdk shell and IndexedDB stores are
 * thin wrappers over this; all the ranking behavior is here so it unit-tests in plain Node.
 */
import type { DocMeta } from '../types.js';
import { adaptiveBoosts, type AdaptiveEntry } from './frecency.js';

export type SwitcherKind = 'note' | 'alias' | 'tag' | 'entity';

export interface SwitcherRow {
  kind: SwitcherKind;
  /** Stable key for frecency/adaptive-history: noteId, entityId, or `tag:<name>`. */
  id: string;
  noteId?: string;
  entityId?: string;
  /** The text the fuzzy matcher scores against (title, alias, tag, or entity name). */
  text: string;
  /** For alias rows: the canonical page title, shown as "Alias → Canonical". */
  canonical?: string;
  icon: string | null;
  updatedAt: number;
  starred: boolean;
}

export interface EntityCandidate {
  entityId: string;
  name: string;
  aliases?: string[];
  icon?: string | null;
  updatedAt?: number;
}

/**
 * Build the flat switcher rows from locally-held data (DocMeta from lapo-meta + entity candidates).
 * One note row per page, one alias row per page alias (badged to its canonical title), one tag row
 * per distinct tag, one entity row per entity (+ its aliases). ~50k rows is well within the scorer's
 * few-ms envelope (spec §4.2).
 */
export function buildSwitcherRows(metas: readonly DocMeta[], entities: readonly EntityCandidate[] = []): SwitcherRow[] {
  const rows: SwitcherRow[] = [];
  const tagUpdated = new Map<string, number>();

  for (const m of metas) {
    const starred = false; // DocMeta doesn't carry starred; the app overlays it from NoteRecord.
    rows.push({ kind: 'note', id: m.noteId, noteId: m.noteId, text: m.title || 'Untitled', icon: m.icon, updatedAt: m.updatedAt, starred });
    for (const alias of m.aliases ?? []) {
      if (!alias.trim()) continue;
      rows.push({ kind: 'alias', id: m.noteId, noteId: m.noteId, text: alias, canonical: m.title || 'Untitled', icon: m.icon, updatedAt: m.updatedAt, starred });
    }
    for (const tag of m.tags ?? []) {
      const t = tag.replace(/^#/, '');
      if (!t) continue;
      tagUpdated.set(t, Math.max(tagUpdated.get(t) ?? 0, m.updatedAt));
    }
  }

  for (const [tag, updatedAt] of tagUpdated) {
    rows.push({ kind: 'tag', id: `tag:${tag}`, text: `#${tag}`, icon: null, updatedAt, starred: false });
  }

  for (const e of entities) {
    rows.push({ kind: 'entity', id: e.entityId, entityId: e.entityId, text: e.name, icon: e.icon ?? null, updatedAt: e.updatedAt ?? 0, starred: false });
    for (const alias of e.aliases ?? []) {
      if (!alias.trim()) continue;
      rows.push({ kind: 'alias', id: e.entityId, entityId: e.entityId, text: alias, canonical: e.name, icon: e.icon ?? null, updatedAt: e.updatedAt ?? 0, starred: false });
    }
  }

  return rows;
}

const WORD_BOUNDARY = /[\s\-_/.:]/;

/**
 * VS Code-style fuzzy score: the query must be a subsequence of the text (case-insensitive). Awards
 * bonuses for matching at the start of the string, at a word boundary, at a camelCase hump, and for
 * consecutive runs; slightly penalizes length beyond the query (tighter matches rank higher). Returns
 * null when the query isn't a subsequence at all. Greedy leftmost matching — deterministic, ~O(n).
 */
export function fuzzyScore(text: string, query: string): number | null {
  const q = query.toLowerCase();
  if (!q) return 0;
  const t = text.toLowerCase();
  let ti = 0;
  let score = 0;
  let run = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const found = t.indexOf(q[qi]!, ti);
    if (found === -1) return null;
    let bonus = 1;
    if (found === 0) bonus += 8;
    const prev = text[found - 1];
    if (prev && WORD_BOUNDARY.test(prev)) bonus += 6;
    const ch = text[found]!;
    if (found > 0 && ch >= 'A' && ch <= 'Z' && prev! >= 'a' && prev! <= 'z') bonus += 4; // camelCase hump
    if (found === ti) {
      run += 1;
      bonus += run * 2;
    } else {
      run = 0;
    }
    score += bonus;
    ti = found + 1;
  }
  return score - Math.max(0, t.length - q.length) * 0.05;
}

/**
 * The character positions in `text` that `query` matches, using the SAME greedy-leftmost subsequence
 * walk as {@link fuzzyScore} (so the highlight lands exactly on the chars that earned the score).
 * Returns null when the query isn't a subsequence of the text at all — callers then fall back to a
 * plain substring highlighter. Deterministic, ~O(n). Used by the palette to `<mark>` a matched title
 * so a title row shows WHY it matched (LENS-05).
 */
export function fuzzyMatchIndices(text: string, query: string): number[] | null {
  const q = query.toLowerCase();
  if (!q) return [];
  const t = text.toLowerCase();
  const out: number[] = [];
  let ti = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const found = t.indexOf(q[qi]!, ti);
    if (found === -1) return null;
    out.push(found);
    ti = found + 1;
  }
  return out;
}

export interface RankContext {
  now: number;
  /** Adaptive (query→pick) history entries. */
  adaptive?: readonly AdaptiveEntry[];
  /** Precomputed frecency per row id (from the opens store). Missing id = 0. */
  frecencyById?: ReadonlyMap<string, number>;
  /** Max rows to return (default 50). */
  limit?: number;
}

/**
 * Rank switcher rows for the typed query, applying the tiers (spec §4.2 / D3):
 *   1. adaptive-history TIER — rows with a (query→pick) memory float above everything else;
 *   2. fuzzy-score tier — VS Code-style match strength;
 *   3. frecency + updatedAt — pure tiebreaks WITHIN an equal fuzzy score (never a score blend).
 * Empty query → frecency-ordered recents (Notion/Obsidian pattern).
 */
export function rankSwitcher(rows: readonly SwitcherRow[], rawQuery: string, ctx: RankContext): SwitcherRow[] {
  const limit = ctx.limit ?? 50;
  const frecencyById = ctx.frecencyById ?? new Map<string, number>();
  const query = rawQuery.trim();

  if (!query) {
    return [...rows]
      .sort(
        (a, b) =>
          (frecencyById.get(b.id) ?? 0) - (frecencyById.get(a.id) ?? 0) ||
          b.updatedAt - a.updatedAt ||
          a.text.localeCompare(b.text),
      )
      .slice(0, limit);
  }

  const boosts = adaptiveBoosts(ctx.adaptive ?? [], query, ctx.now);
  const scored: { row: SwitcherRow; score: number; boost: number; frec: number }[] = [];
  for (const row of rows) {
    const score = fuzzyScore(row.text, query);
    if (score == null) continue;
    scored.push({ row, score, boost: boosts.get(row.id) ?? 0, frec: frecencyById.get(row.id) ?? 0 });
  }

  scored.sort(
    (a, b) =>
      (b.boost > 0 ? 1 : 0) - (a.boost > 0 ? 1 : 0) || // adaptive tier: any boost outranks none
      b.boost - a.boost ||
      b.score - a.score || // fuzzy tier
      b.frec - a.frec || // frecency tiebreak within equal score
      b.row.updatedAt - a.row.updatedAt ||
      a.row.text.localeCompare(b.row.text),
  );
  return scored.slice(0, limit).map((x) => x.row);
}
